import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress, Drawer, IconButton } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import CloseIcon from "@material-ui/icons/Close";
import EmojiObjectsOutlinedIcon from "@material-ui/icons/EmojiObjectsOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import WhatsNewAPIs, { iWhatsNewResponse } from "../../apis/whatsNew.apis";
import BaseAPIs from "../../apis/base.apis";
import { HideChatWithEmailwish, ShowChatWithEmailwish } from "../common";
import FeedbackModal from "./FeedbackModal";
import {
    filterByCategory,
    groupByMonth,
    PreparedPost,
    splitCarousel,
    WhatsNewCategory,
} from "./whatsNewPrepare";
import "./whats-new.scss";

type FilterTab = "all" | WhatsNewCategory;

const TABS: Array<{ id: FilterTab; label: string }> = [
    { id: "all", label: "All" },
    { id: "new", label: "New" },
    { id: "improvements", label: "Improvements" },
    { id: "fixes", label: "Fixes" },
    { id: "beta", label: "Beta" },
];

const MEDIA_TONES = ["mint", "peach", "lavender", "sky"] as const;

function CategoryCapsule({
    category,
    label,
}: {
    category: WhatsNewCategory;
    label: string;
}) {
    return (
        <span className={`campaign-status-badge ew-whats-new-capsule ew-whats-new-capsule--${category}`}>
            {label}
        </span>
    );
}

function PostCta({
    entry,
    variant = "primary",
}: {
    entry: PreparedPost;
    variant?: "primary" | "secondary";
}) {
    const { ctaLabel: label, ctaUrl: url } = entry;
    if (!label || !url) return null;

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`ew-whats-new__cta ew-whats-new__cta--${variant}`}
        >
            {label}
            {variant === "primary" ? <ArrowForwardIcon fontSize="small" /> : null}
        </a>
    );
}

function PostMedia({
    image,
    alt,
    className,
    tone,
}: {
    image?: string;
    alt: string;
    className: string;
    tone: (typeof MEDIA_TONES)[number];
}) {
    return (
        <div className={`${className} ew-whats-new__media--${tone}`}>
            {image ? (
                <img src={image} alt={alt} className="ew-whats-new__media-img" loading="lazy" />
            ) : (
                <div className="ew-whats-new__media-placeholder" aria-hidden />
            )}
        </div>
    );
}

function HeroCarousel({ entries }: { entries: PreparedPost[] }) {
    const [index, setIndex] = useState(0);
    const active = entries[index] || entries[0];

    useEffect(() => {
        setIndex(0);
    }, [entries]);

    useEffect(() => {
        if (entries.length < 2) return undefined;
        const timer = window.setInterval(() => {
            setIndex((current) => (current + 1) % entries.length);
        }, 6500);
        return () => window.clearInterval(timer);
    }, [entries]);

    if (!active) return null;

    const { post, category, categoryLabel, image, summary } = active;

    return (
        <section className="ew-whats-new-carousel" aria-roledescription="carousel">
            <article className="ew-whats-new-hero" aria-live="polite">
                <div className="ew-whats-new-hero__content">
                    <div className="ew-whats-new-hero__meta">
                        <CategoryCapsule category={category} label={categoryLabel} />
                    </div>
                    <h3 className="ew-whats-new-hero__title">{post.title}</h3>
                    {summary ? <p className="ew-whats-new-hero__summary">{summary}</p> : null}
                    <div className="ew-whats-new-hero__actions">
                        <PostCta entry={active} />
                    </div>
                </div>
                <PostMedia
                    image={image}
                    alt={post.title}
                    className="ew-whats-new-hero__media"
                    tone={MEDIA_TONES[index % MEDIA_TONES.length]}
                />
            </article>

            {entries.length > 1 ? (
                <div className="ew-whats-new-carousel__dots" role="tablist" aria-label="Featured updates">
                    {entries.map((entry, dotIndex) => (
                        <button
                            key={entry.post.id}
                            type="button"
                            role="tab"
                            aria-selected={dotIndex === index}
                            aria-label={`Show update ${dotIndex + 1}`}
                            className={
                                dotIndex === index
                                    ? "ew-whats-new-carousel__dot is-active"
                                    : "ew-whats-new-carousel__dot"
                            }
                            onClick={() => setIndex(dotIndex)}
                        />
                    ))}
                </div>
            ) : null}
        </section>
    );
}

/** Fixed-height list row: capsule + title + summary on the left, image on the right. */
function PostRow({
    entry,
    tone,
}: {
    entry: PreparedPost;
    tone: (typeof MEDIA_TONES)[number];
}) {
    const { post, category, categoryLabel, image, summary } = entry;

    return (
        <article className="ew-whats-new-item">
            <div className="ew-whats-new-item__content">
                <CategoryCapsule category={category} label={categoryLabel} />
                <h3 className="ew-whats-new-item__title">{post.title}</h3>
                {summary ? <p className="ew-whats-new-item__summary">{summary}</p> : null}
                <PostCta entry={entry} variant="secondary" />
            </div>
            <PostMedia
                image={image}
                alt={post.title}
                className="ew-whats-new-item__media"
                tone={tone}
            />
        </article>
    );
}

function FeatureIdeaFooter({ onSubmit }: { onSubmit: () => void }) {
    return (
        <div className="ew-whats-new-idea">
            <div className="ew-whats-new-idea__lead">
                <span className="ew-whats-new-idea__icon" aria-hidden>
                    <EmojiObjectsOutlinedIcon fontSize="small" />
                </span>
                <div>
                    <p className="ew-whats-new-idea__title">Have a feature idea?</p>
                    <p className="ew-whats-new-idea__subtitle">We'd love to hear it.</p>
                </div>
            </div>
            <button type="button" className="ew-whats-new-idea__cta" onClick={onSubmit}>
                Submit feedback
                <OpenInNewIcon fontSize="small" />
            </button>
        </div>
    );
}

type Props = {
    open: boolean;
    onClose: () => void;
    onRead?: () => void;
};

export default function WhatsNewDrawer({ open, onClose, onRead }: Props) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<iWhatsNewResponse | null>(null);
    const [tab, setTab] = useState<FilterTab>("all");
    const [feedbackOpen, setFeedbackOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        new WhatsNewAPIs()
            .feed()
            .then((res) => {
                if (cancelled || BaseAPIs.hasError(res)) return;
                setData(res);
                new WhatsNewAPIs().markRead().then(() => onRead && onRead());
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) setTab("all");
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        HideChatWithEmailwish();
        return ShowChatWithEmailwish;
    }, [open]);

    const title = data?.title || "See what's new?";
    const side = data?.settings?.sidebarPosition === "left" ? "left" : "right";
    const { carousel, rest } = useMemo(() => splitCarousel(data?.posts || []), [data]);
    const monthGroups = useMemo(() => groupByMonth(filterByCategory(rest, tab)), [rest, tab]);
    const hasList = monthGroups.length > 0;

    return (
        <>
            <Drawer
                anchor={side}
                open={open}
                onClose={onClose}
                classes={{
                    root: "ew-whats-new__drawer",
                    paper: "ew-whats-new__paper",
                }}
            >
                <div className="ew-whats-new">
                    <header className="ew-whats-new__header">
                        <h2 className="ew-whats-new__heading">{title}</h2>
                        <IconButton
                            aria-label="Close"
                            onClick={onClose}
                            className="ew-whats-new__close"
                            size="small"
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </header>

                    <div className="ew-whats-new__scroll">
                        {loading && (
                            <div className="ew-whats-new__state">
                                <CircularProgress size={28} />
                            </div>
                        )}

                        {!loading && (
                            <>
                                {carousel.length > 0 && <HeroCarousel entries={carousel} />}

                                <div className="ew-whats-new-tabs" role="tablist" aria-label="Update categories">
                                    {TABS.map((item) => (
                                        <button
                                            key={item.id}
                                            type="button"
                                            role="tab"
                                            aria-selected={tab === item.id}
                                            className={
                                                tab === item.id
                                                    ? "ew-whats-new-tabs__tab is-active"
                                                    : "ew-whats-new-tabs__tab"
                                            }
                                            onClick={() => setTab(item.id)}
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </div>

                                {!hasList && (
                                    <p className="ew-whats-new__state">
                                        {carousel.length
                                            ? "Nothing else in this category yet."
                                            : "No announcements yet."}
                                    </p>
                                )}

                                {monthGroups.map((group) => (
                                    <section key={group.key} className="ew-whats-new-month">
                                        <h3 className="ew-whats-new-month__label">{group.label}</h3>
                                        <div className="ew-whats-new-month__card">
                                            {group.entries.map((entry, index) => (
                                                <PostRow
                                                    key={entry.post.id}
                                                    entry={entry}
                                                    tone={MEDIA_TONES[index % MEDIA_TONES.length]}
                                                />
                                            ))}
                                        </div>
                                    </section>
                                ))}
                            </>
                        )}
                    </div>

                    {!loading && (
                        <FeatureIdeaFooter onSubmit={() => setFeedbackOpen(true)} />
                    )}
                </div>
            </Drawer>
            <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
        </>
    );
}
